import { message } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import apiCall from "../../../components/Api";
import AntdBreadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import CommonFIlterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import StatusComponent from "../../../components/Common/StatusComponent";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

const emptyFilterValues = {
  "Lead Status": [],
  "Registered At": [],
};

export default function FranchiseCityTownMasters() {
  const navigate = useNavigate();
  const [sort, setSort] = useState("newest");
  const [sortLabel, setSortLabel] = useState("Sort by");
  const handleSortClick = useSortHandler(setSort, setSortLabel);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearchText, () => {
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
  });
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(emptyFilterValues);
  const [activeFilterCategory, setActiveFilterCategory] =
    useState("Lead Status");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilterValues);

  const [deleteModalState, setDeleteModalState] = useState({
    id: "",
    status: false,
  });

  const [isLoading, setIsLoading] = useState(true);

  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}healthsy-pharmacy/city-town-masters/list`,
      initialParams: {
        sort,
        searchText,
        selectedValues: selectedFilters,
      },
    });

  useEffect(() => {
    fetchData({
      sort,
      searchText,
      selectedValues: selectedFilters,
      skip: pagination.skip,
      limit: pagination.limit,
    });
  }, [sort, searchText, selectedFilters, pagination.skip, pagination.limit]);

  const deleteHandler = useDeleteHandler(
    "healthsy-pharmacy/city-town-masters",
    fetchData,
    setDeleteModalState
  );

  const handleDelete = async (id) => {
    try {
      await deleteHandler(id);
      setDeleteModalState({ id: "", open: false });
      message.success("Item deleted successfully");
    } catch (error) {
      setDeleteModalState({ id: "", open: false });
      message.error("Failed to delete item");
    }
  };

  const handleEdit = (id) => {
    navigate(`/franchise-customers/city-town-masters/form/${id}`);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await apiCall(
        `healthsy-pharmacy/city-town-masters/update/${id}`,
        "PATCH",
        { active: !currentStatus },
        null
      );
      fetchData();
      message.success("Status updated successfully");
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      fixed: "left",
      width: 10,
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
    },
    {
      title: "City / Town",
      dataIndex: "location",
      key: "location",
      fixed: "left",
      width: 250,
    },
    {
      title: "Status & Actions",
      width: 250,
      render: (text, record) => (
        <StatusComponent
          text={text}
          record={record}
          handleEdit={handleEdit}
          setDeleteModal={setDeleteModalState}
          handleActive={(id) => handleToggleActive(id, record.active)}
          title="city_town_masters"
        />
      ),
    },
  ];

  const handlePageChange = usePageChange(pagination, setPagination);

  // Fetch options for filters (kept as-is)
  const fetchAndMap = async (url, mapFn) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}${url}`,
        { noLimit: true }
      );

      return data?.rows?.map(mapFn) || [];
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      return [];
    }
  };

  useEffect(() => {
    if (records) setTimeout(() => setIsLoading(false));
  }, [records]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const leadStatuses = await fetchAndMap(
        "lead-status/list",
        (lead) => lead?.type
      );
      setLeadStatusOptions(leadStatuses);
    };
    fetchFilterOptions();
  }, []);

  const filters = useMemo(
    () => ({
      "Lead Status": [],
      "Registered At": [],
    }),
    [leadStatusOptions]
  );

  const addButtonCfg = {
    id: "addnewcitytown",
    onClick: () => navigate("/franchise-programme/city-town-masters/form"),
    style: { width: "240px" },
    label: "Add New City / Town",
  };

  return (
    <>
      <ListPageLayout
        breadcrumbs={
          <AntdBreadcrumbs
            className="healthsyMasterBreadCrumbs pb-0"
            breadcrumbsList={[
              { text: "Franchise programme", link: "" },
              { text: "City / Town Masters", link: "" },
            ]}
          />
        }
        addButton={<FilledButton props={addButtonCfg} />}
        titles={<></>}
        filters={
          <DoctorsFilterOptions
            limit={pagination.limit}
            sortName={sortLabel}
            search={searchText}
            showFilter={false}
            onChange={debouncedSearch}
            setLimit={(val) =>
              setPagination((prev) => ({
                ...prev,
                limit: val,
                skip: 0,
                currentPage: 1,
              }))
            }
            downloadExcel={false}
            handleSortClick={handleSortClick}
            showSortBy={true}
          />
        }
        tableProps={{
          columns,
          dataSource: records,
          className: "mx-3 pt-2 table-scrollable medicineList_table",
        }}
        totalCount={totalCount}
        pagination={pagination}
        onPageChange={(selected) => handlePageChange({ selected })}
        loader={isLoading}
      >
        {deleteModalState.status && (
          <Deletepopup
            handleDelete={handleDelete}
            id={deleteModalState.id}
            isCloseModalEnable={true}
            handleClose={() => setDeleteModalState({ id: "", open: false })}
          />
        )}

        <CommonFIlterModal
          visible={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={async () => {
            setSelectedFilters(tempFilters);
            setIsFilterModalOpen(false);
            setPagination((prev) => ({
              ...prev,
              skip: 0,
              currentPage: 1,
            }));
          }}
          onReset={() => {
            setSelectedFilters(emptyFilterValues);
            setTempFilters(emptyFilterValues);
            setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
            fetchData();
          }}
          filters={filters}
          selectedCategory={activeFilterCategory}
          setSelectedCategory={setActiveFilterCategory}
          selectedValues={tempFilters}
          setSelectedValues={setTempFilters}
        />
      </ListPageLayout>
    </>
  );
}
